import { IgnitionGame } from './IgnitionGame.js';
import { IgnitionRenderer } from './IgnitionRenderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { Position, Direction, isInside } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable, FinePoint, FineTraceStep } from '../../input/KeyboardController.js';
import { fineToEdge, fineToCell } from '../../input/fineGeometry.js';

export class IgnitionInput extends BaseInput<IgnitionGame, IgnitionRenderer> implements KeyboardControllable {
    private gateMode: boolean = false;

    constructor(
        game: IgnitionGame,
        renderer: IgnitionRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);
        const edge = this.detector.detectEdge(x, y, 0.25, true);
        if (edge && this.game.hasFuse(edge)) {
            this.game.toggleFuse(edge);
            this.onUpdate();
        }
    };

    private handleClick = (e: MouseEvent): void => {
        const { x, y } = this.getCoordinates(e);

        if (this.gateMode && isVortexGame(this.game)) {
            const cell = this.detector.detectCell(x, y);
            if (cell && this.game.toggleVortexGate(cell)) {
                this.onUpdate();
            }
            return;
        }

        const edge = this.detector.detectEdge(x, y, 0.25, true);
        if (edge && this.game.toggleFuse(edge)) {
            this.onUpdate();
            return;
        }

        // Fallback: digit click for path display
        const cell = this.detector.detectCell(x, y);
        if (cell) {
            const digit = this.game.getGrid().getDigitAt(cell);
            if (digit !== null) {
                this.game.toggleSelectedDigit(digit);
                this.onUpdate();
            }
        }
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Fuses sit on edges, so the cursor rides the half-grid like Walls: a tap on an
    // edge toggles that fuse. The trace runs cell to cell, laying a fuse on each edge
    // it crosses — including the rim, where a hop off the grid toggles the rim fuse
    // in place. Fuses can't touch digits, so the trace won't step onto one.

    usesFineCursor(): boolean {
        return true;
    }

    /** Half-segments run from a boundary cell out to the grid edge — rim fuses. */
    allowsFineRim(): boolean {
        return true;
    }

    /** Fire runs along the fuses laid; nothing carries over the edge. */
    wrapsFineCursor(): boolean {
        return false;
    }

    placeAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;

        // A cell center: tapping a digit toggles its ignition path, as clicking does.
        const cell = fineToCell(p);
        if (cell) {
            const digit = this.game.getGrid().getDigitAt(cell);
            if (digit === null) return false;
            this.game.toggleSelectedDigit(digit);
            return true;
        }

        const edge = fineToEdge(this.game.getGrid(), p);
        return !!edge && this.game.toggleFuse(edge);
    }

    deleteAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;
        const edge = fineToEdge(this.game.getGrid(), p);
        if (!edge || !this.game.hasFuse(edge)) return false;
        return this.game.toggleFuse(edge);
    }

    /** Each hop toggles the fuse on the edge crossed — the `moveDir` side of the cell
     *  being left. Off the grid at the rim that's the rim edge, so bouncing against
     *  the boundary lays a half-segment out to it. */
    traceFine({ cell, moveDir }: FineTraceStep): boolean {
        if (this.gateMode) return false;
        const edge = this.game.getGrid().getEdgeFromPosition(cell, moveDir);
        return this.game.toggleFuse(edge);
    }

    /** A fuse can't touch a digit cell, so the trace stops rather than running over
     *  one — the cursor holds and nothing is laid. */
    blocksFineTrace(cell: Position, moveDir: Direction): boolean {
        if (this.gateMode) return false;
        const dest = this.game.getGrid().move(cell, moveDir);
        return isInside(dest) && this.game.getGrid().getDigitAt(dest) !== null;
    }

    /** `X` goes to the central fire source the fuses have to reach back to. */
    fineCursorOrigin(): FinePoint {
        const mid = Math.floor(this.game.getGrid().size / 2) - 1;
        return { r: 2 * mid + 1, c: 2 * mid + 1 };
    }
}
